import PropTypes from 'prop-types';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import moment from 'moment/moment';
import ReactHtmlParser from 'react-html-parser';
import format from 'string-template';
import {
  MenuItem,
} from '@mui/material';
import cloneDeep from 'lodash.clonedeep';
import * as Sentry from '@sentry/browser';

import { isAIMealPlanButtonEnabled } from '../../../../../../utils/postHog';
import useToast from '../../../../../hooks/useToast';
import useComponentMounted from '../../../../../../hooks/useComponentMounted';
import useChallengeSaveConfirmation from '../../../../../hooks/useChallengeSaveConfirmation';
import ChallengePropagationDialog from '../../../../../components/ChallengePropagationDialog';
import LoadingOverlay from '../../../../../components/LoadingOverlay';
import { getMacroAverages, getMealTimesStats } from '../../../../../utils/meals';
import {
  addRecipeToMealTimes,
  bulkAddRecipesToMealTimes,
  removeRecipeFromMealTimes,
  moveRecipeInMealPlan,
  hasMealPlanStructureChanged,
} from '../../../../../utils/mealPlan';
import { DateFormat } from '../../../../../../utils/date';
import {
  AddButton,
  CancelButton,
  SaveButton,
  PrimaryButton,
} from '../../../../../../components/Button/ActionButtons';
import RadioButtonGroup from '../../../../../components/RadioButtonGroup';
import EditItem from '../../../components/EditItem';
import MealPlan, { MealPlanType } from '../../../../../Model/MealPlan';
import UserContext from '../../../../../../context/UserContext';
import { StyledInlineSelect, StyledTextField } from '../../../../../../components/Inputs';
import { CoachingActivity } from '../../../../../../utils/log';
import useLogger from '../../../../../../hooks/useLogger';
import MealPlanContext from '../../../../../context/MealPlanContext';
import { StyledTextBolder } from '../../../../../../styles/text';
import DraggableSortedList from '../EditMealTimesModal/components/DraggableSortedList';
import MealPlanView from '../../../../../components/MealPlanView';
import AIMealPlanModal from '../../../components/AIMealPlanModal';
import {
  ErrorTag,
  InfoTag,
  SuccessTag,
  TransparentTag,
} from '../../../../../../components/Tags';
import useMealPlanGeneration from '../../../../../hooks/useMealPlanGeneration';
import useAIMealPlanGeneration from '../../../../../hooks/useAIMealPlanGeneration';
import AddRecipeModal from '../../../components/AddRecipeModal';
import GenerationStatus from '../../../components/GenerationStatus';
import { HeaderContainer, MealAssignmentsContainer, Title } from '../../styles';
import {
  AlertContainer,
  AlertIcon,
  AlertTypography,
  AnalysisContainer,
} from '../EditMealTimesModal/styles';

import { initialMealPlanValue, initialLiveMealPlanValue, AI_MEAL_PLAN } from './utils';
import {
  TitledContainer,
  TitledContainerBody,
  TitledContainerFoot,
  TitledContainerFootEditor,
  TitledContainerHead,
  TitledContainerHeadDescription,
  TitledContainerHeadTitle,
  MealPlanTemplateTypeSelector,
  FootActionButton,
  AnalysisWrapper,
  ErrorsContainer,
  MealPlanTemplateDisclaimer,
  MealPlanTemplateExcluder,
  InlineSelectRow,
  ItemContainer,
  Label,
  ItemList,
  GenerateAIMealPlanContainer,
} from './styles';
import texts from './texts.json';

const ManageMealTemplate = ({
  onClose,
  selectedMealPlan,
  isAutomationTemplate,
}) => {
  const { showToast } = useToast();
  const { userId } = useContext(UserContext);
  const {
    coachMealPlanViewsCollection: mealPlanViewsCollection,
    saveDefaultMealPlan,
    defaultMealPlan,
  } = useContext(MealPlanContext);

  const [mealPlanIsLive, setMealPlanIsLive] = useState(false);
  const [mealPlanIsAI, setMealPlanIsAI] = useState(false);
  const [mealPlanName, setMealPlanName] = useState(initialMealPlanValue.name);
  const [mealTimes, setMealTimes] = useState(cloneDeep(initialMealPlanValue.mealTimes));
  const [numberOfMealPlansToExclude, setNumberOfMealPlansToExclude] = useState(3);
  const [selectedBucketId, setSelectedBucketId] = useState();
  const [isAddRecipeModalOpen, setIsAddRecipeModalOpen] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const isComponentMountedRef = useComponentMounted();

  const { generateRecipes } = useMealPlanGeneration();
  const { logCoachingActivity } = useLogger();

  const mealBreakdown = useMemo(() => mealTimes.reduce((acc, mealTime) => {
    acc[mealTime.name] = mealTime.caloricSplit;
    return acc;
  }, {}), [mealTimes]);

  const {
    aiMealPlannerRequestInFlight,
    aiMealPlanModalOpen,
    aiMealPlannerRequestId,
    handleCloseAIMealPlanModal,
    openAIMealPlanModal,
    handleGenerationComplete,
    setAIMealPlanModalOpen,
  } = useAIMealPlanGeneration({
    isTemplate: true,
    mealBreakdown,
    setMealTimes,
    errorMessage: texts.aiMealPlanGenerationError,
    successMessage: texts.mealPlanTemplateCreated,
  });

  /* Setting defaults for edit pages */
  useEffect(() => {
    if (selectedMealPlan) {
      setMealPlanName(selectedMealPlan.name);
      setMealTimes([...selectedMealPlan.mealTimes]);
      setLastUpdated(selectedMealPlan.lastUpdated);
      const isLive = selectedMealPlan.type === MealPlanType.LIVE;
      setMealPlanIsLive(isLive);
      if (isLive) {
        setNumberOfMealPlansToExclude(selectedMealPlan.numberOfMealPlansToExclude);
      }
    }
  }, [
    selectedMealPlan,
  ]);

  const macroAverages = useMemo(() => getMacroAverages(mealTimes), [mealTimes]);

  /*
    Get total % of calories allocated.
    Also check for errors, if any meal time has 0% calories allocated, or a meal time is missing a name.
    Meal times cannot have duplicated names.
    Used in the meal times editor.
  */
  const {
    caloriesAllocated,
    noAllocationError,
    noNameError,
    duplicatedNamesError,
    numberOfRecipesError,
  } = useMemo(() => getMealTimesStats(mealTimes), [
    mealTimes,
  ]);

  // Check if a meal time is empty. Return false when the template is a LIVE template
  const hasEmptyMealTime = useMemo(() => (
    !mealPlanIsLive && mealTimes.some((mealTime) => (
      mealTime.meals && mealTime.meals.length === 0
    ))
  ), [
    mealPlanIsLive,
    mealTimes,
  ]);

  const caloriesAllocatedError = caloriesAllocated !== 100;

  const hasErrors = caloriesAllocatedError
    || noAllocationError
    || noNameError
    || duplicatedNamesError
    || !mealPlanName
    || (mealPlanIsLive && numberOfRecipesError);

  const recipesList = useMemo(() => {
    const recipes = [];
    if (!mealPlanIsLive && mealTimes) {
      mealTimes.forEach(({ meals }) => meals.forEach(({ recipe }) => {
        recipes.push(recipe.id);
      }));
    }
    return [...new Set(recipes)];
  }, [
    mealPlanIsLive,
    mealTimes,
  ]);

  /* Handlers */
  const saveMealPlan = useCallback(async () => {
    setIsSaving(true);
    try {
      // Calculate allergen tags for the entire meal plan
      const allAllergenTags = [];
      if (!mealPlanIsLive) {
        mealTimes.forEach(({ meals }) => meals.forEach(({ recipe }) => {
          recipe.allergenTags?.forEach((tag) => allAllergenTags.push(tag));
        }));
      }
      const now = new Date();
      const mealPlanData = {
        name: mealPlanName,
        mealTimes,
        lastUpdated: now,
        ...(isAutomationTemplate && { isAutomationTemplate: true }),
        ...(
          mealPlanIsLive
            ? {
              type: MealPlanType.LIVE,
              numberOfMealPlansToExclude,
            }
            : {
              type: MealPlanType.PREDEFINED,
              allergenTags: [...new Set(allAllergenTags)],
              recipes: recipesList,
              macroAverages,
            }
        ),
      };

      // If editing a PUBLIC meal plan, create a new meal plan instead of updating the original
      if (selectedMealPlan?.isPublic) {
        const mealPlanDoc = await MealPlan.addDoc({
          ...mealPlanData,
          createdAt: now,
          coach: userId,
          originalMealPlan: selectedMealPlan.id,
        });
        logCoachingActivity(CoachingActivity.CREATED_MEAL_PLAN, { mealPlanId: mealPlanDoc.id });

        // The first meal plan created is automatically the default one.
        if (!mealPlanViewsCollection.hasDocs && !defaultMealPlan) {
          await saveDefaultMealPlan(mealPlanDoc.id);
        }

        showToast(texts.mealPlanTemplateCreated);
      } else if (selectedMealPlan) {
        // For non-PUBLIC meal plans, update as normal
        await selectedMealPlan.updateMealPlan(mealPlanData);
        showToast(texts.mealPlanTemplateUpdated);
        logCoachingActivity(CoachingActivity.UPDATED_MEAL_PLAN, { mealPlanId: selectedMealPlan.id });
      } else {
        // Creating a new meal plan
        const mealPlanDoc = await MealPlan.addDoc({
          ...mealPlanData,
          createdAt: now,
          coach: userId,
        });
        logCoachingActivity(CoachingActivity.CREATED_MEAL_PLAN, { mealPlanId: mealPlanDoc.id });

        // The first meal plan created is automatically the default one.
        if (!mealPlanViewsCollection.hasDocs && !defaultMealPlan) {
          await saveDefaultMealPlan(mealPlanDoc.id);
        }

        showToast(texts.mealPlanTemplateCreated);
      }
    } catch (error) {
      // A failed save leaves the editor open and usable, so the coach can retry without losing their edits.
      Sentry.captureException(error);
      showToast(texts.mealPlanTemplateSaveError, { error: true });
      return;
    } finally {
      if (isComponentMountedRef.current) {
        setIsSaving(false);
      }
    }
    onClose();
  }, [
    isComponentMountedRef,
    defaultMealPlan,
    saveDefaultMealPlan,
    mealPlanName,
    userId,
    mealTimes,
    showToast,
    mealPlanViewsCollection.hasDocs,
    macroAverages,
    onClose,
    numberOfMealPlansToExclude,
    mealPlanIsLive,
    selectedMealPlan,
    recipesList,
    logCoachingActivity,
    isAutomationTemplate,
  ]);

  const {
    isChecking: isCheckingChallenge,
    pendingPropagation,
    requestSave,
    confirmSave,
    cancelSave,
  } = useChallengeSaveConfirmation(saveMealPlan);

  const handleSave = useCallback(() => {
    if (hasErrors) {
      return undefined;
    }

    // Only a saved, owned, predefined plan can be attached to a challenge — a new
    // plan, the copy forked from a public one, or a live plan has no client copies.
    // And only a change to what clients are handed reaches them, so nothing else
    // warrants the notice.
    const canReachChallengeClients = !!selectedMealPlan
      && !selectedMealPlan.isPublic
      && !mealPlanIsLive
      && hasMealPlanStructureChanged(selectedMealPlan, { name: mealPlanName, mealTimes });
    return requestSave(canReachChallengeClients ? { mealPlanId: selectedMealPlan.id } : null);
  }, [
    hasErrors,
    selectedMealPlan,
    mealPlanIsLive,
    mealPlanName,
    mealTimes,
    requestSave,
  ]);

  const handleOpenAddRecipeModal = (bucketId = null) => {
    setSelectedBucketId(bucketId);
    setIsAddRecipeModalOpen(true);
  };

  const handleChangeTemplateType = useCallback((value) => {
    const isLive = value === MealPlanType.LIVE;
    const isAI = value === AI_MEAL_PLAN;

    if (isLive === mealPlanIsLive && isAI === mealPlanIsAI) {
      return;
    }

    setMealPlanIsLive(isLive);
    setMealPlanIsAI(isAI);

    // Handle state transitions when switching between meal plan types
    if (isLive) {
      // Switching to LIVE type
      // Always reset to live initial values (different meal times structure with recipesAmount)
      // Keep the current meal plan name
      setMealTimes(cloneDeep(initialLiveMealPlanValue.mealTimes));
    } else if (mealPlanIsLive) {
      // Switching to AI/PREDEFINED from LIVE
      // Reset to predefined initial values (meal times with meals array instead of recipesAmount)
      // Keep the current meal plan name
      setMealTimes(cloneDeep(initialMealPlanValue.mealTimes));
    }
    // When switching between AI and PREDEFINED, keep current meal plan values (no reset)
  }, [
    mealPlanIsLive,
    mealPlanIsAI,
  ]);

  const handleGenerateMeals = useCallback((mealTimeName) => {
    const recipes = generateRecipes({ mealTimeName, excludeRecipes: recipesList });
    setMealTimes((prev) => bulkAddRecipesToMealTimes(prev, mealTimeName, recipes));
  }, [
    generateRecipes,
    recipesList,
  ]);

  const handleAddRecipe = (bucketId, recipe) => {
    setMealTimes((prev) => addRecipeToMealTimes(prev, bucketId, recipe));
  };

  const handleDeleteRecipe = (recipeId, bucketIndex) => {
    setMealTimes((prev) => removeRecipeFromMealTimes(prev, recipeId, bucketIndex));
  };

  const handleMoveRecipe = ({
    removedIndex,
    addedIndex,
    sourceBucketIndex,
    destinationBucketIndex,
    meal,
  }) => {
    setMealTimes((prevMealTimes) => moveRecipeInMealPlan(
      prevMealTimes,
      removedIndex,
      addedIndex,
      sourceBucketIndex,
      destinationBucketIndex,
      meal,
    ));
  };

  /* Small render chunks */
  const renderError = (errorText) => (
    <AlertContainer>
      <AlertIcon />
      <AlertTypography>{errorText}</AlertTypography>
    </AlertContainer>
  );

  const AnalysisTag = caloriesAllocatedError ? ErrorTag : SuccessTag;
  const analysis = (
    <AnalysisContainer>
      <AnalysisTag>
        {`${caloriesAllocated}${texts.caloriesAllocated}`}
      </AnalysisTag>
      <TransparentTag>{texts.in}</TransparentTag>
      <InfoTag>{`${mealTimes.length} ${texts.mealTimes}`}</InfoTag>
    </AnalysisContainer>
  );

  const actionButtons = (
    <>
      <SaveButton onClick={handleSave} disabled={hasErrors || hasEmptyMealTime}>
        {texts.saveTemplate}
      </SaveButton>
      <CancelButton onClick={onClose}>
        {texts.cancel}
      </CancelButton>
    </>
  );

  const excludeMealsOptions = [1, 2, 3, 4, 5, 6, 7].map((key) => (
    <MenuItem key={key} value={key}>{key}</MenuItem>
  ));

  const mealPlanTypeOptions = [
    {
      label: texts.createPredefinedPlan,
      value: MealPlanType.PREDEFINED,
    },
    {
      label: texts.createLivePlan,
      value: MealPlanType.LIVE,
    },
  ];

  if (isAIMealPlanButtonEnabled()) {
    mealPlanTypeOptions.push({
      label: texts.createAIMealPlan,
      value: AI_MEAL_PLAN,
    });
  }

  const newMacroAverages = useMemo(() => (
    getMacroAverages(mealTimes || [])
  ), [mealTimes]);

  const selectedMealPlanType = (() => {
    if (mealPlanIsAI) {
      return AI_MEAL_PLAN;
    }
    if (mealPlanIsLive) {
      return MealPlanType.LIVE;
    }
    return MealPlanType.PREDEFINED;
  })();

  return (
    <>
      <MealAssignmentsContainer>
        <HeaderContainer>
          <Title>{selectedMealPlan ? texts.updateMealPlan : texts.createMealPlan}</Title>
        </HeaderContainer>
        <TitledContainer>
          <TitledContainerHead>
            <TitledContainerHeadTitle>{texts.containerTitle}</TitledContainerHeadTitle>
            {lastUpdated && (
              <TitledContainerHeadDescription>
                {`${texts.lastUpdated} ${moment(lastUpdated.toDate()).format(DateFormat.DATE_FORMAT_COMMA_TH)}`}
              </TitledContainerHeadDescription>
            )}
          </TitledContainerHead>
          <TitledContainerBody>
            <EditItem label={texts.mealPlanName}>
              <StyledTextField
                value={mealPlanName}
                onChange={({ target }) => setMealPlanName(target.value)}
                disabled={isAutomationTemplate}
                autoFocus
              />
            </EditItem>
            <DraggableSortedList
              mealTimes={mealTimes}
              setMealTimes={setMealTimes}
              templateType={mealPlanIsLive ? MealPlanType.LIVE : MealPlanType.PREDEFINED}
            />
            <AnalysisWrapper>
              {analysis}
            </AnalysisWrapper>
            {hasErrors && (
              <ErrorsContainer>
                {caloriesAllocatedError && renderError(texts.totalCaloriesAlert)}
                {noAllocationError && renderError(texts.noAllocationAlert)}
                {noNameError && renderError(texts.noNameAlert)}
                {(duplicatedNamesError && !noNameError) && renderError(texts.duplicatedNames)}
                {mealPlanIsLive && numberOfRecipesError && renderError(texts.numberOfRecipesError)}
              </ErrorsContainer>
            )}
          </TitledContainerBody>
          <TitledContainerFoot>
            {!selectedMealPlan && (
              <>
                <MealPlanTemplateTypeSelector>
                  <RadioButtonGroup
                    options={mealPlanTypeOptions}
                    selectedOption={selectedMealPlanType}
                    onOptionChange={handleChangeTemplateType}
                  />
                </MealPlanTemplateTypeSelector>
                {mealPlanIsAI ? (
                  <GenerateAIMealPlanContainer>
                    <ItemContainer>
                      <PrimaryButton
                        onClick={openAIMealPlanModal}
                        size="medium"
                        disabled={aiMealPlannerRequestInFlight}
                      >
                        {aiMealPlannerRequestInFlight ? texts.generatingAIMealPlan : texts.generateAIMealPlan}
                      </PrimaryButton>
                    </ItemContainer>
                    <Label>
                      {texts.generateAIMealPlanDescription}
                    </Label>
                  </GenerateAIMealPlanContainer>
                ) : (
                  <MealPlanTemplateDisclaimer>
                    <StyledTextBolder>
                      {mealPlanIsLive ? texts.disclaimerLiveTitle : texts.disclaimerPreTitle}
                    </StyledTextBolder>
                    {mealPlanIsLive ? texts.disclaimerLiveDescription : texts.disclaimerPreDescription}
                  </MealPlanTemplateDisclaimer>
                )}
              </>
            )}
            {mealPlanIsLive && (
              <MealPlanTemplateExcluder>
                <EditItem label={texts.recipeVariety} noMarginsOnLabel>
                  <InlineSelectRow>
                    {texts.excludeMealsP1}
                    <StyledInlineSelect
                      value={numberOfMealPlansToExclude}
                      onChange={({ target }) => setNumberOfMealPlansToExclude(target.value)}
                    >
                      {excludeMealsOptions}
                    </StyledInlineSelect>
                    {texts.excludeMealsP2}
                  </InlineSelectRow>
                </EditItem>
              </MealPlanTemplateExcluder>
            )}
          </TitledContainerFoot>
          <TitledContainerFootEditor>
            {!mealPlanIsLive && (
              <>
                <FootActionButton>
                  <AddButton onClick={() => handleOpenAddRecipeModal()}>{texts.addRecipes}</AddButton>
                  {!!newMacroAverages?.percentages?.protein && (
                    <ItemContainer>
                      <Label>{texts.macros}</Label>
                      <ItemList>
                        <InfoTag>
                          {ReactHtmlParser(format(texts.protein, { percentage: newMacroAverages.percentages.protein }))}
                        </InfoTag>
                        <InfoTag>
                          {ReactHtmlParser(format(texts.carbs, { percentage: newMacroAverages.percentages.carbs }))}
                        </InfoTag>
                        <InfoTag>
                          {ReactHtmlParser(format(texts.fat, { percentage: newMacroAverages.percentages.fat }))}
                        </InfoTag>
                      </ItemList>
                    </ItemContainer>
                  )}
                </FootActionButton>
                <MealPlanView
                  alwaysShowAddButton
                  mealTimes={mealTimes}
                  onAddRecipe={handleOpenAddRecipeModal}
                  allowEdit
                  moveMeal={handleMoveRecipe}
                  handleDelete={handleDeleteRecipe}
                  onGenerateMeals={handleGenerateMeals}
                />
              </>
            )}
          </TitledContainerFootEditor>
          <TitledContainerFoot>
            <FootActionButton>
              {actionButtons}
            </FootActionButton>
          </TitledContainerFoot>
        </TitledContainer>
        <AddRecipeModal
          isOpen={isAddRecipeModalOpen}
          onAddRecipe={handleAddRecipe}
          mealTimes={mealTimes}
          onClose={() => setIsAddRecipeModalOpen(false)}
          selectedBucketId={selectedBucketId}
        />
        <AIMealPlanModal
          open={aiMealPlanModalOpen}
          setOpen={setAIMealPlanModalOpen}
          handleClose={handleCloseAIMealPlanModal}
          isTemplate
        />
        <ChallengePropagationDialog
          propagation={pendingPropagation}
          title={texts.challengeUpdateTitle}
          content={texts.challengeUpdateContent}
          onConfirm={confirmSave}
          onCancel={cancelSave}
        />
        <LoadingOverlay isLoading={isCheckingChallenge || isSaving} />
      </MealAssignmentsContainer>
      {aiMealPlannerRequestId && (
        <GenerationStatus
          requestId={aiMealPlannerRequestId}
          onMealPlanGenerationComplete={handleGenerationComplete}
        />
      )}
    </>
  );
};

ManageMealTemplate.propTypes = {
  onClose: PropTypes.func.isRequired,
  selectedMealPlan: PropTypes.object,
  isAutomationTemplate: PropTypes.bool,
};

ManageMealTemplate.defaultProps = {
  selectedMealPlan: null,
  isAutomationTemplate: false,
};

export default ManageMealTemplate;
